import React, { useEffect, useRef, useState } from "react";

// Convert a data URL to a Blob without using fetch() — works on iOS Safari & Android
function dataUrlToBlob(dataUrl) {
  const [header, b64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/png";
  const binary = atob(b64);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// Always produces a PNG File — broadest app compatibility for sharing
function makeShareFile(imageData) {
  try {
    const blob = dataUrlToBlob(imageData);
    return new File([blob], "hello-sticker.png", { type: "image/png" });
  } catch {
    return null;
  }
}

async function doNativeShare(imageData) {
  const shareUrl = window.location.origin + "/hello-stickers";
  const shareTitle = "Hello Sticker \u2014 Mike Terpeza";
  const shareText =
    "Check out this sticker from Mike Terpeza! Come say hello \ud83d\udc4b";
  const file = makeShareFile(imageData);

  // Skip canShare() — it's unreliable on Android. Just try directly.
  if (file && navigator.share) {
    try {
      await navigator.share({
        files: [file],
        title: shareTitle,
        text: shareText,
        url: shareUrl,
      });
      return;
    } catch (err) {
      if (err?.name === "AbortError") return;
      // File share unsupported — try URL-only
    }
  }
  if (navigator.share) {
    try {
      await navigator.share({
        title: shareTitle,
        text: shareText,
        url: shareUrl,
      });
      return;
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
  }
  // Final fallback: download
  const a = document.createElement("a");
  a.href = imageData;
  a.download = "hello-sticker.png";
  a.click();
}

// For a social platform button: try native share (image included via OS sheet),
// fall back to download + URL intent + show tip when native share unavailable.
async function shareToSocial(imageData, platformUrl, setTip) {
  const shareUrl = window.location.origin + "/hello-stickers";
  const file = makeShareFile(imageData);

  if (navigator.share) {
    if (file) {
      try {
        await navigator.share({
          files: [file],
          title: "Hello Sticker \u2014 Mike Terpeza",
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    try {
      await navigator.share({
        title: "Hello Sticker \u2014 Mike Terpeza",
        url: shareUrl,
      });
      return;
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
  }

  // No native share (desktop): download image + open platform URL + show tip
  const a = document.createElement("a");
  a.href = imageData;
  a.download = "hello-sticker.png";
  a.click();
  window.open(platformUrl, "_blank", "noopener,noreferrer");
  if (setTip) setTip("Image downloaded \u2014 attach it to your post!");
}

export default function StickerShareMenu({ imageData, stickerId }) {
  const [open, setOpen] = useState(false);
  const [tip, setTip] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTip("");
    const handleOutside = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  if (!imageData) return null;

  // Use unique sticker URL when we have an ID — this makes link previews show
  // the actual sticker image on LinkedIn, iMessage, Twitter, etc.
  const shareUrl = stickerId
    ? `${window.location.origin}/hello-stickers/${stickerId}`
    : `${window.location.origin}/hello-stickers`;
  const tweetText = encodeURIComponent(
    "Check out this sticker from Mike Terpeza!",
  );
  const twitterUrl = `https://twitter.com/intent/tweet?text=${tweetText}&url=${encodeURIComponent(shareUrl)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="relative inline-block" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#ff4000] dark:bg-[#6cebe4] text-white dark:text-gray-900 text-sm font-bold hover:brightness-95 transition-colors select-none"
        aria-haspopup="true"
        aria-expanded={open}
      >
        {"Share \u2197"}
      </button>

      {tip && (
        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-max max-w-[220px] rounded-lg bg-gray-900 border border-white/20 px-3 py-2 text-xs text-white text-center shadow-xl z-30">
          {tip}
        </div>
      )}

      {open && (
        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-950 border border-white/15 rounded-xl overflow-hidden shadow-2xl z-20 min-w-[200px]">
          {/* Share with image — native OS share sheet, picks up the sticker */}
          <button
            type="button"
            onClick={() => {
              doNativeShare(imageData);
              setOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-white/10 transition-colors border-b border-white/10"
          >
            <span>{"📤"}</span>
            <span className="font-semibold">Share with image</span>
          </button>

          <div className="px-4 pt-2.5 pb-1 text-[10px] text-white/40 uppercase tracking-wider">
            Post to platform
          </div>

          {/* Facebook */}
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-white hover:bg-white/10 transition-colors border-b border-white/10"
          >
            <span className="font-bold text-[15px] leading-none w-4 text-center">
              f
            </span>
            <span>Facebook</span>
          </a>

          {/* Instagram — saves image for manual upload */}
          <button
            type="button"
            onClick={() => {
              const a = document.createElement("a");
              a.href = imageData;
              a.download = "hello-sticker.png";
              a.click();
              setOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white hover:bg-white/10 transition-colors border-b border-white/10"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              className="flex-shrink-0"
            >
              <defs>
                <linearGradient id="ig-ssm" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#f9ce34" />
                  <stop offset="30%" stopColor="#ee2a7b" />
                  <stop offset="100%" stopColor="#6228d7" />
                </linearGradient>
              </defs>
              <rect
                x="2"
                y="2"
                width="20"
                height="20"
                rx="5"
                ry="5"
                stroke="url(#ig-ssm)"
                strokeWidth="2"
              />
              <circle
                cx="12"
                cy="12"
                r="4.5"
                stroke="url(#ig-ssm)"
                strokeWidth="2"
              />
              <circle cx="17.5" cy="6.5" r="1.2" fill="url(#ig-ssm)" />
            </svg>
            <span>Instagram</span>
          </button>

          {/* X / Twitter */}
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-white hover:bg-white/10 transition-colors border-b border-white/10"
          >
            <span className="font-black text-base leading-none w-4 text-center">
              {"\ud835\udd4f"}
            </span>
            <span>Post on X</span>
          </a>

          {/* LinkedIn */}
          <a
            href={linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-white hover:bg-white/10 transition-colors border-b border-white/10"
          >
            <span className="font-bold text-[13px] leading-none w-4 text-center">
              in
            </span>
            <span>LinkedIn</span>
          </a>

          {/* Download */}
          <button
            type="button"
            onClick={() => {
              const a = document.createElement("a");
              a.href = imageData;
              a.download = "hello-sticker.png";
              a.click();
              setOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white hover:bg-white/10 transition-colors"
          >
            <span className="w-4 text-center">{"⬇️"}</span>
            <span>Download PNG</span>
          </button>
        </div>
      )}
    </div>
  );
}
